import { useEffect, useMemo, useState } from 'react'
import { LoaderFunctionArgs, useLoaderData } from 'react-router-dom'
import { useCurrentHealthCenter } from '../../components/AppShell'
import { InterviewFormData, createInterview, fetchInterviewForm, updateInterview } from '../../api/records'
import { Answers, Recipe } from '../../interviews/types'
import { InterviewTemplateView } from '../../interviews/InterviewTemplate'
import { getTemplate } from '../../interviews/templates'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { Alert, DateField, Dropdown, ErrorList, PageHeading, Panel, PanelBody, SubmitButton } from '../../components/common'
import { useImpression } from '../../lib/useImpression'
import { useNavigateTo } from '../../lib/spaPath'
import styles from './InterviewFormPage.module.scss'

function todayIso(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function loader({ params, request }: LoaderFunctionArgs): Promise<InterviewFormData> {
  const { searchParams } = new URL(request.url)
  return fetchInterviewForm(Number(params.healthCenterId), {
    id: params.interviewId,
    clinicalRecordId: searchParams.get('clinical_record_id') ?? '',
    templateSessionKey: searchParams.get('template_session_key') ?? undefined,
  })
}

function getInitialAnswers(form: InterviewFormData, autosaveKey: string, readonly: boolean): { date: string; answers: Answers } {
  const loaded = { date: form.interview?.date ?? todayIso(), answers: form.interview?.answers ?? {} }
  const saved = readonly ? null : window.localStorage.getItem(autosaveKey)
  if (!saved) {
    return loaded
  }
  try {
    const draft = JSON.parse(saved) as { date?: string; answers?: Answers }
    return { date: draft.date || loaded.date, answers: { ...loaded.answers, ...draft.answers } }
  } catch {
    window.localStorage.removeItem(autosaveKey)
    return loaded
  }
}

export default function InterviewFormPage({ readonly = false }: { readonly?: boolean }) {
  const healthCenter = useCurrentHealthCenter()
  const form = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const autosaveKey = `hc-autosave:${window.location.pathname}${window.location.search}`
  const [initial] = useState(() => getInitialAnswers(form, autosaveKey, readonly))
  const [date, setDate] = useState<string>(initial.date)
  const [answers, setAnswers] = useState<Answers>(initial.answers)
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const navigateTo = useNavigateTo()

  useEffect(() => {
    if (!readonly) {
      window.localStorage.setItem(autosaveKey, JSON.stringify({ date, answers }))
    }
  }, [date, answers, readonly, autosaveKey])

  const definition = useMemo(() => {
    if (!form.template) {
      return null
    }
    return getTemplate(form.template.profession_key, form.template.session_key, form.template.version_key)
  }, [form])

  const showFirstInterviewHint = form.patients_count <= 1 && !readonly
  useImpression('Onboarding Hint Shown', showFirstInterviewHint ? { hint: 'first_interview' } : null)

  if (!healthCenter) {
    return null
  }

  if (!form.template || !definition) {
    return <p className="text-center">No se pudo cargar la plantilla de la ficha</p>
  }

  const template = form.template

  const handleSubmit = async () => {
    if (submitting || readonly) {
      return
    }
    setSubmitting(true)
    setErrors([])
    try {
      const payload = {
        date,
        clinical_record_id: form.clinical_record_id,
        session_key: template.session_key,
        version_key: template.version_key,
        answers,
      }
      const result = form.interview
        ? await updateInterview(healthCenter.id, form.interview.id, payload)
        : await createInterview(healthCenter.id, payload)
      const templateKey = `${template.session_key}/${template.version_key}`
      trackEvent(form.interview ? 'Interview Updated' : 'Interview Created', { template_key: templateKey }, { transport: 'sendBeacon' })
      window.localStorage.removeItem(autosaveKey)
      navigateTo(result.redirect, { notice: result.notice })
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as Record<string, string[] | string> | null) : null
      const requestMessage = typeof body?.error === 'string' ? body.error : null
      const fieldErrors = body && !requestMessage ? body : null
      trackEvent('Form Save Failed', {
        form: 'interview',
        reason: fieldErrors ? 'validation' : 'request',
        status_code: requestError instanceof ApiError ? requestError.status : undefined,
        fields: fieldErrors ? Object.keys(fieldErrors) : undefined,
      })
      setErrors(
        fieldErrors
          ? Object.entries(fieldErrors).flatMap(([field, messages]) => [messages].flat().map((message) => `${field} ${message}`))
          : [requestMessage ?? 'Ocurrió un error'],
      )
      setSubmitting(false)
      window.scrollTo({ top: 0 })
    }
  }

  const title = form.interview
    ? `Ficha Nº${form.interview.session_number ?? 0} - ${template.human_session_name}`
    : `Nueva Ficha Nº${form.new_interview_number ?? 1} - ${template.human_session_name}`

  const previousLink = (id: number) =>
    `/health_centers/${healthCenter.id}/interviews/${id}?clinical_record_id=${form.clinical_record_id}`

  return (
    <form
      id="interview-form"
      onSubmit={(event) => {
        event.preventDefault()
        void handleSubmit()
      }}
    >
      {showFirstInterviewHint && (
        <Alert kind="info" className={styles.firstAlert}>
          Ingresa los antecedentes de la nueva sesión.
          <br />
          Cuando termines haz click abajo en el boton "GUARDAR FICHA".
        </Alert>
      )}
      <div className="col-xs-12 col-md-8 col-md-offset-2">
        <Panel variant="plain" className="panel-primary" style={{ marginBottom: 80 }}>
          <PanelBody>
            <ErrorList messages={errors} />
            <PageHeading level={2}>{title}</PageHeading>
            <div className="col-xs-12">
              <DateField
                label="Fecha"
                labelStyle="plain"
                className="control-group"
                value={date}
                readOnly={readonly}
                onChange={setDate}
              />
            </div>
            <br />
            <div className="interview-show">
              <InterviewTemplateView
                definition={definition}
                answers={answers}
                disabled={readonly}
                onChange={(key, value) => setAnswers((current) => ({ ...current, [key]: value }))}
                onRecipesChange={(recipes: Recipe[]) => setAnswers((current) => ({ ...current, recipes }))}
                recipeFieldLabels={form.recipe_field_labels}
                recipeTypeLabels={form.recipe_type_labels}
                recipeDefaultOption={form.recipe_default_option}
              />
            </div>
          </PanelBody>
        </Panel>
      </div>
      {!readonly && (
        <div style={{ width: '100vw', position: 'fixed', bottom: 10, left: 0, textAlign: 'center', paddingTop: 20 }}>
          {(form.previous_complete_id || form.previous_id) && (
            <Dropdown
              className="dropup fullscreen-xs"
              style={{ display: 'inline-block' }}
              buttonStyle={{ width: '100%' }}
              label="Ver ficha anterior"
              showCaret={false}
            >
                {form.previous_complete_id && (
                  <li>
                    <a href={previousLink(form.previous_complete_id)} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('Previous Interview Viewed', { scope: 'full' })}>
                      Ficha Completa
                    </a>
                  </li>
                )}
                {form.previous_id && (
                  <li>
                    <a href={previousLink(form.previous_id)} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('Previous Interview Viewed', { scope: 'previous' })}>
                      Ficha Previa
                    </a>
                  </li>
                )}
            </Dropdown>
          )}
          <SubmitButton
            label="Guardar Ficha"
            className="fullscreen-xs no-margin-xs"
            style={{ marginLeft: 20, width: '30%' }}
            disabled={submitting}
          />
        </div>
      )}
    </form>
  )
}
